// Konto und Sicherheit (Pflichtenheft 4.2, M02/M03/M05): MFA, Sitzungen, Passwort, MFA-Pflicht.
import { useQuery } from '@tanstack/react-query';
import { ApiError, api, request } from './client';
import { can, useMe } from './queries';
import type { Me } from './types';

export interface MfaStatus {
  enabled: boolean;
  confirmedAt: string | null;
  recoveryCodesRemaining: number;
  required: boolean;
  requiredBy: 'platform' | 'tenant' | null;
  available: boolean;
}
export interface TotpSetup { secret: string; otpauthUri: string; qrSvg: string; qr: { dim: number; path: string } }
export interface SessionRow { id: string; createdAt: string; lastSeenAt: string; ip: string | null; userAgent: string | null; current: boolean; supportTenant: string | null }
export type MfaPolicy = 'none' | 'admins' | 'all';

export const ak = {
  mfa: ['account', 'mfa'] as const,
  sessions: ['account', 'sessions'] as const,
  tenantPolicy: ['account', 'tenant-mfa-policy'] as const,
  platformSecurity: ['account', 'platform-security'] as const,
};

export const useMfaStatus = () => useQuery({ queryKey: ak.mfa, queryFn: () => api.get<MfaStatus>('/auth/mfa') });
export const useSessions = () => useQuery({ queryKey: ak.sessions, queryFn: async () => (await api.get<{ items: SessionRow[] }>('/auth/sessions')).items });
export function useTenantMfaPolicy() {
  const { data: me } = useMe();
  return useQuery({
    queryKey: [...ak.tenantPolicy, me?.activeTenantId ?? 'none'],
    queryFn: async () => (await api.get<{ requiredFor: MfaPolicy }>('/tenant/mfa-policy')).requiredFor,
    enabled: can(me, 'tenant.settings.manage'),
  });
}
export const usePlatformSecurity = () => useQuery({ queryKey: ak.platformSecurity, queryFn: () => api.get<{ mfaPlatformAdmins: boolean }>('/platform/security') });

export const startTotpSetup = () => api.post<TotpSetup>('/auth/mfa/totp/setup');
export const confirmTotp = (code: string) => api.post<{ recoveryCodes: string[]; me: Me }>('/auth/mfa/totp/confirm', { code });
export const verifyMfa = (method: 'totp' | 'recovery', code: string) => api.post<Me>('/auth/mfa/verify', { method, code });
export const disableMfa = (password: string, code: string) => request<void>('DELETE', '/auth/mfa/totp', { body: { password, code } });

/** Fehlermeldung des Servers (Titel bzw. Feldfehler), z. B. für 409/422 mit eigenem Text. */
export function serverMessage(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  const fields = e.problem.errors ? Object.values(e.problem.errors).flat().filter(Boolean) : [];
  if (fields.length) return fields.join(' ');
  return e.code === 'conflict' || e.code === 'validation_failed' || e.status === 503 ? e.message : null;
}

/** Gerät/Browser aus dem User-Agent, kurz („Chrome · Windows“). */
export function deviceLabel(ua: string | null): string {
  if (!ua) return '–';
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : null;
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : null;
  return [browser, os].filter(Boolean).join(' · ') || ua.slice(0, 40);
}
